"use client";

import { useRef } from "react";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(useGSAP, ScrollTrigger);

interface Props {
  children: React.ReactNode;
  className?: string;
}

// Scopes all GSAP behaviour for a chain landing page:
//  - [data-reveal]        : fades/translates up once it scrolls into view
//  - [data-reveal-group]  : its direct children reveal with a stagger
//  - [data-hover]         : scales/lifts on hover ("scale" | "lift")
//  - [data-shine]         : the masked light sweep across the logo (loops)
// Entrance + shine respect prefers-reduced-motion; hover is always allowed.
export default function ChainAnimations({ children, className }: Props) {
  const root = useRef<HTMLDivElement>(null);

  useGSAP(
    () => {
      const mm = gsap.matchMedia();

      mm.add("(prefers-reduced-motion: no-preference)", () => {
        gsap.utils.toArray<HTMLElement>("[data-reveal]").forEach((el) => {
          gsap.from(el, {
            y: 40,
            autoAlpha: 0,
            duration: 0.9,
            ease: "power3.out",
            scrollTrigger: { trigger: el, start: "top 88%", once: true },
          });
        });

        gsap.utils.toArray<HTMLElement>("[data-reveal-group]").forEach((group) => {
          gsap.from(Array.from(group.children), {
            y: 48,
            autoAlpha: 0,
            duration: 0.85,
            ease: "power3.out",
            stagger: 0.12,
            scrollTrigger: { trigger: group, start: "top 85%", once: true },
          });
        });

        const shine = root.current?.querySelector<HTMLElement>("[data-shine]");
        if (shine) {
          gsap.set(shine, { backgroundPositionX: "160%", autoAlpha: 0 });
          gsap
            .timeline({ repeat: -1, repeatDelay: 2.4 })
            .to(shine, { autoAlpha: 1, duration: 0.4 })
            .to(
              shine,
              { backgroundPositionX: "-60%", duration: 1.6, ease: "power2.inOut" },
              "<"
            )
            .to(shine, { autoAlpha: 0, duration: 0.5 }, ">-0.3");
        }
      });

      // Hover (asset interactions) — kept subtle, allowed even with reduced motion.
      const cleanups: Array<() => void> = [];
      gsap.utils.toArray<HTMLElement>("[data-hover]").forEach((el) => {
        const lift = el.dataset.hover === "lift";
        const enter = () =>
          gsap.to(el, {
            scale: lift ? 1.03 : 1.06,
            y: lift ? -6 : 0,
            duration: 0.3,
            ease: "power2.out",
          });
        const leave = () =>
          gsap.to(el, { scale: 1, y: 0, duration: 0.3, ease: "power2.out" });
        el.addEventListener("mouseenter", enter);
        el.addEventListener("mouseleave", leave);
        cleanups.push(() => {
          el.removeEventListener("mouseenter", enter);
          el.removeEventListener("mouseleave", leave);
        });
      });

      return () => cleanups.forEach((c) => c());
    },
    { scope: root }
  );

  return (
    <div ref={root} className={className}>
      {children}
    </div>
  );
}
